import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { HiCalendar } from 'react-icons/hi2';
import { cn } from '../../../utils/formatter';
import type { Feriado } from '../../../models';
import { HOLIDAY_COLORS } from './LegendPanel';
import type { HolidaysByType } from '../hooks/useHolidayFilters';

interface HolidaysListProps {
  holidaysByType: HolidaysByType;
  counts: {
    NACIONAL: number;
    ESTADUAL: number;
    MUNICIPAL: number;
    FOLGA: number;
    total: number;
  };
  onHolidayClick?: (feriado: Feriado) => void;
  onEditClick?: (feriado: Feriado) => void;
  isAdmin: boolean;
}

const HOLIDAY_LABELS = {
  NACIONAL: 'Feriados Nacionais',
  ESTADUAL: 'Feriados Estaduais',
  MUNICIPAL: 'Feriados Municipais',
  FOLGA: 'Folgas'
};

export const HolidaysList: React.FC<HolidaysListProps> = ({
  holidaysByType,
  counts,
  onHolidayClick,
  onEditClick,
  isAdmin
}) => {
  const renderHolidayItem = (feriado: Feriado) => {
    const colors = HOLIDAY_COLORS[feriado.tipo];
    const feriadoDate = new Date(feriado.data + 'T00:00:00');

    return (
      <div
        key={feriado.id}
        className={cn(
          'p-2 rounded-lg border-l-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors',
          colors.border,
          colors.bg
        )}
        onClick={() => onHolidayClick?.(feriado)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className={cn('text-sm font-medium line-clamp-1', colors.text)}>
              {feriado.descricao}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {format(feriadoDate, "dd 'de' MMMM", { locale: ptBR })}
            </p>
          </div>
          {isAdmin && onEditClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(feriado);
              }}
              className="text-xs text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
              title="Editar feriado"
            >
              Editar
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-neutral-700">
        <div className="flex items-center gap-2 mb-1">
          <HiCalendar className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Feriados
          </h3>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          {counts.total} {counts.total === 1 ? 'feriado' : 'feriados'} neste período
        </p>
      </div>

      {/* List */}
      <div className="p-3 max-h-[calc(100vh-400px)] overflow-y-auto">
        {counts.total === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <HiCalendar className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nenhum feriado neste período</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(HOLIDAY_LABELS).map(([tipo, label]) => {
              const holidays = holidaysByType[tipo as keyof HolidaysByType];
              const count = counts[tipo as keyof Omit<typeof counts, 'total'>];

              if (count === 0) return null;

              return (
                <div key={tipo}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      {label}
                    </h4>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400">
                      {count}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {holidays.map(renderHolidayItem)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

